'use client';

import { useMediaQuery } from '@mnd-frontend/hooks';
import { MEDIA_LARGE, NEUTRAL_WHITE, PURPLE_500, PURPLE_1000 } from '@mnd-frontend/ui';
import styled from 'styled-components';
import { NewsletterSubscriptionForm } from './newsletter-subscription-form';

const StyledFooter = styled.footer`
  background-color: ${PURPLE_1000};
  color: ${NEUTRAL_WHITE};
  display: flex;
  align-items: center;
  flex-direction: column;
  svg {
    margin-bottom: 4rem;
  }
`;

const Container = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  padding: 4rem 1rem;
  width: 100%;
  margin: 0 auto;
  gap: 2rem;
  @media (min-width: ${MEDIA_LARGE}px) {
    max-width: 1280px;
    grid-template-rows: unset;
    grid-template-columns: 60% 1fr;
    padding: 6rem 1rem;
  }
`;

const SignupHeader = styled.h2`
  font-size: 2rem;
  @media (min-width: ${MEDIA_LARGE}px) {
    font-size: 3rem;
  }
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.05ch;
  color: ${NEUTRAL_WHITE};
  text-transform: uppercase;
`;

const SubHeader = styled.span`
  font-size: 1.125rem;
  @media (min-width: ${MEDIA_LARGE}px) {
    font-size: 1.5rem;
  }
  line-height: 1;
  font-weight: 700;
  color: ${PURPLE_500};
  letter-spacing: -0.05ch;
`;

const Info = styled.div`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const MndLogo = () => (
  <svg
    width="182"
    height="32"
    viewBox="0 0 182 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#clip0_1655_2584)">
      <path
        d="M168.38 0V15.1099L174.368 7.84006H180.287L172.905 16.8204L181.122 26.7987H175.203L168.38 18.531V26.7987H163.366V0H168.38Z"
        fill="white"
      />
      <path
        d="M150.051 20.5283C150.26 21.1698 150.886 21.74 151.931 22.3814C152.906 23.0229 153.951 23.308 155.065 23.308C156.875 23.308 157.71 22.7378 157.71 21.5261C157.71 21.241 157.641 20.956 157.502 20.7421C157.362 20.5283 157.084 20.3145 156.805 20.172C156.457 19.9581 156.178 19.8869 155.969 19.7443C155.761 19.673 155.343 19.5305 154.855 19.388C154.368 19.2454 154.089 19.1741 153.951 19.1029C153.951 19.1029 153.672 19.0316 153.254 18.889C152.836 18.7465 152.488 18.6752 152.279 18.5326C152.07 18.4614 151.722 18.3189 151.235 18.105C150.748 17.8912 150.4 17.6774 150.121 17.5349C149.842 17.321 149.494 17.1072 149.146 16.8221C148.798 16.537 148.449 16.1806 148.31 15.8243C148.101 15.4679 147.893 15.1115 147.753 14.6126C147.614 14.185 147.545 13.6861 147.545 13.1872C147.545 11.2628 148.241 9.83732 149.633 8.83954C151.026 7.84169 152.836 7.41406 154.995 7.41406C156.875 7.41406 158.546 7.77043 159.869 8.55443C161.192 9.33843 162.167 10.265 162.654 11.4053L159.381 13.5435C159.033 13.0446 158.476 12.5457 157.571 12.0468C156.666 11.5479 155.761 11.3341 154.855 11.3341C153.254 11.3341 152.488 11.9043 152.488 12.9734C152.488 13.2584 152.558 13.5435 152.697 13.7574C152.836 13.9712 153.115 14.185 153.393 14.3275C153.741 14.5414 154.02 14.6126 154.229 14.7552C154.438 14.8264 154.786 14.969 155.273 15.1115C155.761 15.2541 156.039 15.3254 156.179 15.3966C156.179 15.3966 156.387 15.4679 156.736 15.5392L157.432 15.753C157.571 15.8243 157.85 15.8955 158.198 16.0381C158.546 16.1806 158.824 16.3232 159.103 16.4658C159.312 16.6083 159.59 16.7509 159.938 16.9646C160.287 17.1785 160.565 17.3923 160.774 17.6061C160.983 17.8199 161.192 18.0338 161.471 18.3189C161.749 18.6039 161.957 18.889 162.097 19.1741C162.236 19.4592 162.375 19.8156 162.515 20.172C162.654 20.5283 162.654 20.956 162.654 21.3123C162.654 23.308 161.957 24.8047 160.635 25.8738C159.312 26.9429 157.432 27.4418 154.995 27.4418C152.836 27.4418 151.096 27.0142 149.633 26.0876C148.171 25.1611 147.197 24.092 146.709 22.8091L150.051 20.5283Z"
        fill="white"
      />
      <path
        d="M131.798 10.2621C133.469 8.40894 135.697 7.48242 138.482 7.48242C141.198 7.48242 143.357 8.40894 144.958 10.2621C146.56 12.1152 147.395 14.4672 147.395 17.3894V18.8149H134.096C134.305 20.0977 134.862 21.0956 135.697 21.8083C136.533 22.5211 137.508 22.8774 138.692 22.8774C140.641 22.8774 142.103 22.236 143.078 20.8817L146.49 23.4476C145.654 24.6592 144.61 25.5858 143.217 26.2985C141.825 27.0113 140.293 27.3676 138.622 27.3676C135.837 27.3676 133.539 26.4411 131.798 24.588C130.058 22.7349 129.152 20.3116 129.152 17.4606C129.292 14.4672 130.127 12.1152 131.798 10.2621ZM134.165 15.5363H142.66C142.451 14.3246 141.964 13.3981 141.198 12.6854C140.502 11.9726 139.597 11.6163 138.413 11.6163C137.23 11.6163 136.324 11.9726 135.558 12.6854C134.931 13.3268 134.444 14.2534 134.165 15.5363Z"
        fill="white"
      />
      <path
        d="M111.957 10.2633C113.628 8.41022 115.717 7.55495 118.224 7.55495C120.451 7.55495 122.192 8.33895 123.585 9.83569V0H128.598V26.87H123.585V24.8743C122.262 26.4423 120.451 27.1551 118.224 27.1551C115.717 27.1551 113.559 26.2285 111.957 24.3754C110.286 22.5223 109.45 20.1703 109.45 17.3194C109.45 14.4684 110.286 12.1164 111.957 10.2633ZM114.324 17.3906C114.324 18.8161 114.742 20.0278 115.647 20.9543C116.483 21.8809 117.597 22.3798 118.989 22.3798C120.382 22.3798 121.496 21.8809 122.331 20.9543C123.167 20.0278 123.585 18.8161 123.585 17.3906C123.585 15.9652 123.167 14.7535 122.331 13.827C121.496 12.9004 120.382 12.4015 118.989 12.4015C117.597 12.4015 116.483 12.9004 115.647 13.827C114.742 14.7535 114.324 15.9652 114.324 17.3906Z"
        fill="white"
      />
      <path
        d="M96.8458 20.5283C97.055 21.1698 97.6814 21.74 98.7262 22.3814C99.7007 23.0229 100.745 23.308 101.859 23.308C103.67 23.308 104.505 22.7378 104.505 21.5261C104.505 21.241 104.435 20.956 104.296 20.7421C104.157 20.5283 103.879 20.3145 103.6 20.172C103.252 19.9581 102.973 19.8869 102.764 19.7443C102.556 19.673 102.138 19.5305 101.65 19.388C101.163 19.2454 100.884 19.1741 100.745 19.1029C100.745 19.1029 100.467 19.0316 100.049 18.889C99.631 18.7465 99.2829 18.6752 99.0743 18.5326C98.8651 18.4614 98.517 18.3189 98.0295 18.105C97.5425 17.8912 97.1938 17.6774 96.9155 17.5349C96.6372 17.321 96.2891 17.1072 95.941 16.8221C95.5929 16.537 95.2443 16.1806 95.1054 15.8243C94.8962 15.4679 94.6876 15.1115 94.5481 14.6126C94.4093 14.185 94.3395 13.6861 94.3395 13.1872C94.3395 11.2628 95.0357 9.83732 96.428 8.83954C97.8209 7.84169 99.631 7.41406 101.79 7.41406C103.67 7.41406 105.341 7.77043 106.663 8.55443C107.987 9.33843 108.962 10.265 109.449 11.4053L106.176 13.5435C105.828 13.0446 105.271 12.5457 104.366 12.0468C103.461 11.5479 102.556 11.3341 101.65 11.3341C100.049 11.3341 99.2829 11.9043 99.2829 12.9734C99.2829 13.2584 99.3526 13.5435 99.4921 13.7574C99.631 13.9712 99.9099 14.185 100.188 14.3275C100.536 14.5414 100.815 14.6126 101.024 14.7552C101.232 14.8264 101.581 14.969 102.068 15.1115C102.556 15.2541 102.834 15.3254 102.973 15.3966C102.973 15.3966 103.182 15.4679 103.53 15.5392L104.227 15.753C104.366 15.8243 104.645 15.8955 104.993 16.0381C105.341 16.1806 105.619 16.3232 105.897 16.4658C106.107 16.6083 106.385 16.7509 106.733 16.9646C107.081 17.1785 107.36 17.3923 107.569 17.6061C107.778 17.8199 107.987 18.0338 108.265 18.3189C108.544 18.6039 108.752 18.889 108.892 19.1741C109.031 19.4592 109.17 19.8156 109.31 20.172C109.449 20.5283 109.449 20.956 109.449 21.3123C109.449 23.308 108.752 24.8047 107.43 25.8738C106.107 26.9429 104.227 27.4418 101.79 27.4418C99.631 27.4418 97.8906 27.0142 96.428 26.0876C94.9659 25.1611 93.9914 24.092 93.5039 22.8091L96.8458 20.5283Z"
        fill="white"
      />
      <path
        d="M65.5781 7.83984H70.8004L74.4212 19.5286L78.3901 7.83984H82.1498L86.1186 19.5286L89.6697 7.83984H94.892L88.8341 26.8698H83.8907L80.2002 15.6799L76.5097 26.8698H71.5663L65.5781 7.83984Z"
        fill="white"
      />
      <path
        d="M51.6605 10.2621C53.3317 8.40894 55.5598 7.48242 58.3449 7.48242C61.0602 7.48242 63.219 8.40894 64.8205 10.2621C66.422 12.1152 67.2576 14.4672 67.2576 17.3894V18.8149H53.9583C54.1672 20.0977 54.7243 21.0956 55.5598 21.8083C56.3953 22.5211 57.3701 22.8774 58.5538 22.8774C60.5035 22.8774 61.9655 22.236 62.9406 20.8817L66.3522 23.4476C65.5166 24.6592 64.4724 25.5858 63.0795 26.2985C61.6872 27.0113 60.1555 27.3676 58.4842 27.3676C55.699 27.3676 53.4013 26.4411 51.6605 24.588C49.9198 22.7349 49.0146 20.3116 49.0146 17.4606C49.0843 14.4672 49.9198 12.1152 51.6605 10.2621ZM54.0279 15.5363H62.5228C62.3136 14.3246 61.8267 13.3981 61.0602 12.6854C60.3641 11.9726 59.459 11.6163 58.2753 11.6163C57.1612 11.6163 56.1865 11.9726 55.4205 12.6854C54.7939 13.3268 54.2368 14.2534 54.0279 15.5363Z"
        fill="white"
      />
      <path
        d="M30.9766 7.8398H36.0595V9.6929C37.3128 8.26743 39.0535 7.55469 41.2816 7.55469C43.2312 7.55469 44.9719 8.19616 46.3645 9.4078C47.7571 10.6194 48.523 12.6151 48.523 15.2522V26.8697H43.5097V16.0362C43.5097 14.5394 43.092 13.4704 42.326 12.8289C41.5601 12.1874 40.7246 11.9023 39.7498 11.9023C38.775 11.9023 37.9394 12.1874 37.1735 12.8289C36.4076 13.4704 36.0595 14.5394 36.0595 16.0362V26.8697H31.0462V7.8398H30.9766Z"
        fill="white"
      />
      <path
        d="M20.1282 13.6836L23.2615 21.88L26.4644 13.6836H30.0851L22.983 32.0008H19.5712L21.5904 26.7266L16.5771 13.6836H20.1282Z"
        fill="white"
      />
      <path
        d="M15.5272 15.2516C14.6221 14.3251 13.4384 13.8262 11.8369 13.8262C10.4444 13.8262 9.26066 14.1825 8.42511 14.9665C7.51994 14.2538 6.40587 13.8262 5.01328 13.8262C3.48145 13.8262 2.22812 14.3251 1.32295 15.2516C0.417773 16.1782 0 17.3186 0 18.6728V27.0117H3.20293V19.0291C3.20293 18.3164 3.34219 17.8175 3.69033 17.5324C4.03848 17.2473 4.45625 17.1048 4.94365 17.1048C5.43105 17.1048 5.84883 17.2473 6.19696 17.5324C6.61477 17.8175 6.82361 18.3164 6.82361 19.0291V27.0117H10.0265V19.0291C10.0265 18.3164 10.1658 17.8175 10.514 17.5324C10.8621 17.2473 11.2798 17.1048 11.7673 17.1048C12.2547 17.1048 12.6724 17.2473 13.0206 17.5324C13.3688 17.8175 13.508 18.3164 13.508 19.0291V27.0117H16.7109V18.6728C16.7109 17.2308 16.3628 16.1782 15.5272 15.2516Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="clip0_1655_2584">
        <rect width="181.551" height="32" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const SummaryFooter: React.FC<{
  subheader: string | null;
  header: string | null;
  cta: string | null;
}> = ({ subheader, header, cta }) => {
  const isMobile = useMediaQuery(`@media (max-width: ${MEDIA_LARGE}px)`);
  return (
    <StyledFooter>
      <Container>
        <Info>
          <div>
            <SubHeader>{subheader}</SubHeader>
            <SignupHeader>{header}</SignupHeader>
          </div>
          {!isMobile && <MndLogo />}
        </Info>
        <NewsletterSubscriptionForm ctaText={cta} />
      </Container>
      {isMobile && <MndLogo />}
    </StyledFooter>
  );
};
